import { FormField } from "src/features/calculator/components/field/FormField"
import React, { useEffect } from "react"
import { CalculationSchema } from "src/features/calculator/schema"
import { UseFormSetValue, UseFormWatch, FieldErrors } from "react-hook-form"
import styled from "styled-components"
import {
  calcCustomerTransferFeeRate,
  calcTotalReimbursement,
  calcSelfPayment,
  calcCustomerTransferFee,
} from "src/features/calculator/utils/formulas/basicFormula"
import { CustomerTransferFeeRateRow } from "src/features/calculator/components/formula/row/CustomerTransferFeeRateRow"
import { TotalReimbursementRow } from "src/features/calculator/components/formula/row/TotalReimbursementRow"
import { SelfPaymentRow } from "src/features/calculator/components/formula/row/SelfPaymentRow"
import { CustomerTransferFeeRow } from "src/features/calculator/components/formula/row/CustomerTransferFeeRow"
import {
  calcExCustomerTransferFeeRate,
  calcRemittance,
} from "src/features/calculator/utils/formulas/housePartnerFormula"
import { RemittanceEqualReimbursementRow } from "src/features/calculator/components/formula/row/RemittanceEqualReimbursementRow"
import { useCalculatedResult } from "src/features/calculator/hooks/useCalculatedResult"
import { useFormValueUpdate } from "src/features/calculator/hooks/useFormValueUpdate"

type Props = {
  setValue: UseFormSetValue<CalculationSchema>
  watch: UseFormWatch<CalculationSchema>
  errors: FieldErrors<CalculationSchema>
}

export const HousePartnerFormula = ({ setValue, watch, errors }: Props) => {
  const watchedValues = watch()
  const {
    customerTransferFeeRate,
    totalReimbursement,
    customerTransferFee,
    exCustomerTransferFeeRate,
    consumptionTaxRate,
    maxAdvancePayment,
    initialCost,
    downPayment,
    rent,
    monthlyTransferCount,
  } = watchedValues

  const calculatedResult = useCalculatedResult({
    values: watchedValues,
    setValue,
    calculation: {
      customerTransferFeeRate: calcCustomerTransferFeeRate({
        exCustomerTransferFeeRate,
        consumptionTaxRate,
      }),
      totalReimbursement: calcTotalReimbursement({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      selfPayment: calcSelfPayment({
        maxAdvancePayment,
        initialCost,
        downPayment,
      }),
      customerTransferFee: calcCustomerTransferFee({
        rent,
        customerTransferFeeRate,
      }),
      remittance: calcRemittance({
        totalReimbursement,
        customerTransferFee,
      }),
      exCustomerTransferFeeRate: calcExCustomerTransferFeeRate({
        monthlyTransferCount,
      }),
    },
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.customerTransferFeeRate,
    currentValue: customerTransferFeeRate,
    fieldName: "customerTransferFeeRate",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.totalReimbursement,
    currentValue: totalReimbursement,
    fieldName: "totalReimbursement",
    setValue,
  })

  useFormValueUpdate({
    calculatedValue: calculatedResult.exCustomerTransferFeeRate,
    currentValue: exCustomerTransferFeeRate,
    fieldName: "exCustomerTransferFeeRate",
    setValue,
  })

  return (
    <StyledCalculatorDiv>
      <CustomerTransferFeeRateRow
        customerTransferFeeRate={calculatedResult.customerTransferFeeRate}
        exCustomerTransferFeeRate={exCustomerTransferFeeRate}
        consumptionTaxRate={consumptionTaxRate}
        setValue={setValue}
        errors={errors}
      />
      <TotalReimbursementRow
        totalReimbursement={calculatedResult.totalReimbursement}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <SelfPaymentRow
        selfPayment={calculatedResult.selfPayment}
        maxAdvancePayment={maxAdvancePayment}
        initialCost={initialCost}
        downPayment={downPayment}
        setValue={setValue}
        errors={errors}
      />
      <CustomerTransferFeeRow
        customerTransferFee={calculatedResult.customerTransferFee}
        rent={rent}
        customerTransferFeeRate={customerTransferFeeRate}
        setValue={setValue}
        errors={errors}
      />
      <RemittanceEqualReimbursementRow
        remittance={calculatedResult.remittance}
        totalReimbursement={totalReimbursement}
        setValue={setValue}
        errors={errors}
      />
      <StyledCalculatorRowDiv>
        <FormField
          label="送客手数料率(税抜)"
          type="number"
          placeholder="Placeholder"
          hint={`${calculatedResult.exCustomerTransferFeeRate.toLocaleString()}円`}
          disabled
          value={calculatedResult.exCustomerTransferFeeRate}
        />
        <span>=もし</span>
        <FormField
          label="同月の総客数"
          type="number"
          placeholder="Placeholder"
          hint={`${monthlyTransferCount.toLocaleString()}人`}
          onChange={(e) => {
            setValue("monthlyTransferCount", Number(e.target.value))
          }}
          errorText={errors?.monthlyTransferCount?.message}
          value={monthlyTransferCount}
        />
        <span>{">"} 10 ならば 0.6 それ以外は 0.55</span>
      </StyledCalculatorRowDiv>
    </StyledCalculatorDiv>
  )
}

const StyledCalculatorDiv = styled("div")`
  display: flex;
  flex-direction: column;
  gap: 10px;
`

const StyledCalculatorRowDiv = styled("div")`
  display: flex;
  flex-direction: row;
  gap: 10px;
  align-items: center;
`
